import React, { useContext } from 'react';
import { Progress } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { LangContext } from '../../contexts/langContext';
import { SocialContext } from '../../contexts/socialContext';
import { StateContext } from '../../contexts/stateContext';
import { getGodFaceByGate } from '../../static/crosses_dictionary';
import { Txt } from '../UiKit/Txt';
import { getCrossType, handleGateClick } from '../utilsDesign';
import { getCrossAngleCompatibility } from '../utilsHdCompat';
import { DescriptionIco } from '../UiKit/DescriptionIco';
import GateFocused from '../UiKit/GateFocused';

const PROGRESS_COLORS = {
  match: '#3fb56b',
  mismatch: '#cf5b5b',
  neutral: '#d9a441',
};

const getCrossCompatLabelKey = (result) => {
  if (!result) return null;
  if (result.complementary) return 'cross_compat_complement';
  if (result.hasJuxtaposition) return 'cross_compat_juxt';
  if (result.same && result.angleA === 'right') return 'cross_compat_both_right';
  if (result.same && result.angleA === 'left') return 'cross_compat_both_left';
  if (result.same) return 'cross_compat_same';
  return 'cross_compat_diff';
};

/** Centered compatibility strip for right/left/juxtaposition cross angles. */
export const CrossCompat = ({ designA, designB, $ui: $uiProp }) => {
  const context = { ...useContext(LangContext) };
  const $ui = $uiProp || context.$ui;
  const result = getCrossAngleCompatibility(designA?.profile, designB?.profile);
if (!result) return null;

  const detailLabel = $ui(getCrossCompatLabelKey(result));
  const scoreText = `${result.score}/${result.maxScore}`;
  const progressPercent = Math.round((result.score / result.maxScore) * 100);

  return (
    <div className={`x_penta_compat ${result.status ? `x_penta_compat--${result.status}` : ''}`}>
      <span className="x_penta_compat_line x_penta_compat_line--1"></span>
      <div className={`x_penta_compat_inner x_penta_compat_inner--${result.status}`}>
        <div className="x_penta_compat_progress">
          <span className="x_penta_compat_label">
            <span className="x_penta_compat_pair">{detailLabel}</span>
            <span className="x_penta_compat_score_wrapper">
            <span className="x_penta_compat_score">{scoreText}</span>
            &nbsp;&nbsp;
            <Progress
              percent={progressPercent}
              steps={result.maxScore}
              size="small"
              showInfo={false}
              strokeColor={PROGRESS_COLORS[result.status] || PROGRESS_COLORS.neutral}
            />
            </span>
          </span>
        </div>
      </div>
      <span className="x_penta_compat_line x_penta_compat_line--2"></span>
    </div>
  );
};

export const Cross = React.memo(({ 
  id, design, 
  withDescription = true, 
  className, 
  showGatesPreview = false 
}) => {
  const context = { ...useContext(SocialContext), ...useContext(StateContext), ...useContext(LangContext) };
  const $ui = context.$ui;
  const $cross = context.$cross;

  const getCross = (what) => {
    if (!design.profile) return '';
    const leftRightJuxt = getCrossType(design.profile);
    const crossId = `${leftRightJuxt}_${design.inc_cross.toString()}`;
    return $cross(crossId, what);
  };

  const getGateEmoji = (index) => {
    const emo = ['🌞', '🌎', '🌞', '🌎'];
    return emo[index];
  };

  const location = useLocation();
  const navigate = useNavigate();
  const handleGateClickInner = (gateNumber) => {
    context.setState.setSearch([gateNumber]);
    handleGateClick(gateNumber, location, navigate);
  };

  const angle = getCrossType(design['profile']);

  return (
    <>
      <div className={`x_edu_content x_edu_content--cross ${className ? `x_edu_content--${className}` : ''}`}>
        {/* <div className={`x_edu_content_inner ${className ? `x_edu_content_inner--${className}` : ''}`}> */}
          <div className={`x_edu_white0 ${className ? `x_edu_white0--${className}` : ''}`}>
            <div className="x_edu_1st">
              <div className="x_edu_content_h x_edu_content_h--3 _fgap04">
                {$ui(`cross_${angle}`)}
                {!withDescription && (
                  <DescriptionIco>
                    <div className="x_phs_txt">
                      {angle === 'right' && <Txt>{$ui('cross_right', 'rich')}</Txt>}
                      {angle === 'left' && <Txt>{$ui('cross_left', 'rich')}</Txt>}
                      {angle === 'juxtaposition' && <Txt>{$ui('cross_juxtaposition', 'rich')}</Txt>}
                    </div>
                  </DescriptionIco>
                )}
              </div>

              <div className="x_edu_content_h x_edu_content_h--type">
                {getCross('title')}
              </div>
              {!!(getCross('description')) && (
                <div className='x_edu_item_desc'>
                  <Txt>
                    {getCross('description')}
                  </Txt>
                </div>
              )}

              <div className="x_phs_item1_transference">

                  <div className="x_edu_cross_faces0">
                    {design['inc_cross'] && design['inc_cross'].map((gateNumber, index) => {
                      return (
                        <div
                          className={index > 1 ? "x_edu_cross_face x_edu_cross_face--des" : "x_edu_cross_face"}
                          style={{ position: 'relative' }}
                          key={`${index}_${getGateEmoji(index)}`}
                        >
                          {showGatesPreview && <div className="x_gate_cross_preview">
                            <GateFocused gateId={gateNumber} design={design} zoom={1}/>
                          </div>}
                          <div
                            className="x_click x_click_pop"
                            onClick={() => handleGateClickInner(gateNumber, location, navigate)}
                          />
                          <div className="x_edu_cross_face_sun">
                            {getGateEmoji(index)}
                          </div>
                          <div className="x_edu_cross_face_gate">
                            {gateNumber}
                          </div>
                          <div className="x_edu_cross_face_name">
                            {getGodFaceByGate(gateNumber)['face']}
                          </div>
                          <div>
                            <div className="x_edu_cross_hex">
                              {getGodFaceByGate(gateNumber)['hexagram']}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>


                {withDescription && (
                  <div className="x_phs_item1_transference">
                    <div className="x_phs_false0">
                      {angle === 'right' && <Txt>{$ui('cross_right', 'rich')}</Txt>}
                      {angle === 'left' && <Txt>{$ui('cross_left', 'rich')}</Txt>}
                      {angle === 'juxtaposition' && <Txt>{$ui('cross_juxtaposition', 'rich')}</Txt>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        {/* </div> */}
      </div>
    </>
  );
});
